import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonGroup } from "../../../core/components/buttons/ButtonGroup";
import { InfoText } from "../../../core/components/texts/InfoText";
import { clearPendingReleaseLogin } from "../../../core/utils/pendingReleaseLogin";
import { isFramed, postToDashboard, STAFF_FILL_MESSAGE } from "../../../core/utils/staffFillFrame";
import { useReturnAfterIdle } from "../../../core/utils/useReturnAfterIdle";
import { auth } from "../../../firebaseConfig";
import { handleLogout } from "../../navigation/utils/handleLogout";
import { ReleaseDoneRouteState } from "../../navigation/utils/webAppSession";
import { OnboardingScreenContainer } from "../../onboarding/OnboardingScreenContainer";

const RETURN_TO_START_IDLE_MS = 60_000;

/** Where every practice-release session ends (PF-6133); `{ ended: true }` marks an exit before the last form. */
export const ReleaseDoneScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routeState = useLocation().state as ReleaseDoneRouteState | null;
  const ended = routeState?.ended === true;
  const dashboardOrigin = routeState?.dashboardOrigin;

  useEffect(() => {
    clearPendingReleaseLogin();
    // Every exit signs out before it navigates here; this covers one that did not.
    if (auth.currentUser) void handleLogout();
  }, []);

  useEffect(() => {
    // The embedding dashboard closes the frame on this signal.
    if (dashboardOrigin && isFramed()) postToDashboard({ type: STAFF_FILL_MESSAGE.done }, dashboardOrigin);
  }, [dashboardOrigin]);

  // The link's practice and forms survive the logout, so /welcome is this link's own start, not a foreign one.
  const returnToStart = () => navigate("/welcome", { replace: true });
  // This screen is only ever reached with the session already ended, so the return timer always runs.
  useReturnAfterIdle(RETURN_TO_START_IDLE_MS, returnToStart);

  return (
    <OnboardingScreenContainer hideBackButton>
      <InfoText
        headline={t(ended ? "RELEASE_ENDED_HEADLINE" : "RELEASE_DONE_HEADLINE")}
        text={[t(ended ? "RELEASE_ENDED_TEXT" : "RELEASE_DONE_TEXT")]}
      />
      <StyledButtonGroup
        progressButton={{
          text: t("RELEASE_DONE_RETURN_BUTTON"),
          onClick: returnToStart,
        }}
      />
    </OnboardingScreenContainer>
  );
};

// Same placement as SuccessScreen's progress button (SuccessScreen.tsx).
const StyledButtonGroup = styled(ButtonGroup)`
  align-items: flex-end;
  width: 100%;
`;
